import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// Style
import './DropdownPrice.scss';

// Classnames
import clsx from 'clsx';

// Components
import Img from 'components/Img';
import NoData from 'components/NoData';
import Tabs from 'components/UiKit/Tabs';
import Tile from 'components/UiKit/Tile';

// Type
import { TPrices } from 'pages/MoleculeProduct/reducer';

type TArg = {
	type?: string;
	value?: string;
	mg?: number | null;
	price?: number | null;
}

// Interface
interface IDropdownPrice {
	onChange?: any;
	titlePOA?: string;
	options?: any;
	typePrice?: string;
	className?: string;
	currentPrice?: any;
	innerTitle?: string;
	withUrlParam?: boolean;
	innerTitleClass?: string;
	optionsRadio?: { [index: string]: string }[];
	handlerChangeTypePrice?: (type: string) => void;
}

const DropdownPrice: React.FC<IDropdownPrice> = (props) => {
	const {
		options,
		onChange,
		titlePOA,
		typePrice,
		className,
		innerTitle,
		withUrlParam,
		optionsRadio,
		currentPrice,
		innerTitleClass,
		handlerChangeTypePrice
	} = props;

	const listRef = useRef<HTMLDivElement>(null);
	const labelRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [dropdownPosition, setDropdownPosition] = useState<{
		top: number;
		left: number;
		width: number;
	}>({ top: 0, left: 0, width: 0 });

	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as HTMLElement;

		if (
			target.closest('.field-radio__input') ||
			target.closest('.field-radio__wrapper') ||
			target.closest('.dropdown-price__wrapper-list')
		) {
			return;
		}

		if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
			setIsOpen(false);
		}
	};

	useEffect(() => {
		window.addEventListener('click', handleClickOutside);

		return () => {
			window.removeEventListener('click', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (isOpen && dropdownRef.current) {
			const rect = dropdownRef.current.getBoundingClientRect();
			const scrollY = window.scrollY;
			const scrollX = window.scrollX;

			setDropdownPosition({
				top: rect.bottom + scrollY,
				left: rect.left + scrollX,
				width: rect.width
			});
		}
	}, [isOpen]);

	const handlerClick = () => {
		setIsOpen(!isOpen);
	};

	const handlerClickCurrent = (selected: TArg): void => {
		onChange && onChange(selected);

		setIsOpen(false);
	};

	const handlerChangeRadio = (value: string) => {
		handlerChangeTypePrice && handlerChangeTypePrice(value);
	};

	const getTitlePOA = () => {
		return <strong
			title={titlePOA}
			className="dropdown-price__required"
		>
			*
		</strong>;
	};

	const cnWrapper = clsx(
		'dropdown-price relative',
		className ? className : ''
	);

	const cnWrapperList = clsx(
		'dropdown-price__wrapper-list',
		isOpen ? 'dropdown-price__wrapper-list--active' : ''
	);

	const cnArrow = clsx(
		'dropdown-price__arrow ml5',
		isOpen ? 'dropdown-price__arrow--active' : ''
	);

	const findElem = options?.find((item: TPrices) => item?.mg === currentPrice?.mg);
	const value = findElem?.mg
		? `$${findElem?.priceUsd}/${currentPrice?.mg} mg`
		: 'Pick...';

	const inlineStyle: { [index: string]: string } = {
		zIndex: '997',
		left: `${dropdownPosition.left}px`,
		top: `${dropdownPosition.top + 5}px`
	};

	const content = options?.length ? options?.map((item: TPrices, i: number) => <Tile
		{...item}
		type="Usd"
		key={`${item?.value}_${i}`}
		current={currentPrice?.value}
		onClick={handlerClickCurrent}
	/>) : <NoData text="No available data" />;

	const tabs = optionsRadio?.length
		? optionsRadio?.map(item => {
			return {
				url: item.value,
				label: item.label,
				content: content
			};
		}) : [];

	const hasMultipleTabs = tabs?.length > 1;

	const cnTitle = clsx(
		'dropdown-price__title',
		innerTitleClass,
	);

	return (
		<div ref={dropdownRef} className={cnWrapper}>
			{!!titlePOA && getTitlePOA()}
			<div
				ref={labelRef}
				onClick={handlerClick}
				className="dropdown-price__controll flex flex-ai-center flex-jc-between"
			>
				<div className="dropdown-price__controll flex flex-ai-center flex-jc-between full-width">
					<p className="dropdown-price__title">{value}</p>
					{innerTitle
						? <p
							className={cnTitle}
							dangerouslySetInnerHTML={{ __html: innerTitle }}
						/>
						: hasMultipleTabs && <p className={cnTitle}>({typePrice})</p>
					}
				</div>
				<Img
					isSvg
					alt="Arrow"
					className={cnArrow}
					name="arrowDropdownSvg"
				/>
			</div>

			{isOpen && dropdownPosition.top && dropdownPosition.left && ReactDOM.createPortal(
				<div
					ref={listRef}
					style={inlineStyle}
					className={cnWrapperList}
				>
					<h4 className="dropdown-price__title-list mb10">
						Prices (USD)
					</h4>

					{hasMultipleTabs
						? <Tabs
							tabs={tabs}
							type="secondary"
							nameKeyTab="EBC"
							withUrlParam={withUrlParam}
							callback={handlerChangeRadio}
							defaultScreen={typePrice || 'EBC'}
							classNameContent="dropdown-price__list flex flex-wrap flex-ai-start flex-jc-start flex-jc-between-start-desktop mt10"
						/>
						: <div
							className="dropdown-price__list flex flex-wrap flex-ai-start flex-jc-start flex-jc-between-start-desktop mt10">
							{content}
						</div>}
				</div>,
				document.body
			)}
		</div>
	);
};

export default React.memo(DropdownPrice);
